import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import * as authApi from "./api";
import type { User } from "./api";

type AuthState = {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const TOKEN_KEY = "maestro_auth_token";
const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authApi.me().then((result) => {
      if (result) {
        setUser(result.user);
        setToken(result.token);
        sessionStorage.setItem(TOKEN_KEY, result.token);
      } else {
        setUser(null);
      }
      setLoading(false);
    });
  }, []);

  async function login(email: string, password: string) {
    const { user: u, token: t } = await authApi.login(email, password);
    setUser(u);
    setToken(t);
    sessionStorage.setItem(TOKEN_KEY, t);
  }

  async function logout() {
    await authApi.logout();
    setUser(null);
    setToken(null);
    sessionStorage.removeItem(TOKEN_KEY);
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
